import { useCallback, useEffect, useRef, useState } from 'react'

import styled from 'styled-components'
import type SwiperCore from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'

import 'swiper/css/bundle'

import { IconStatsClose } from 'assets/svg/sprite'
import { usePreviewContext } from 'context/previewContext'
import PreviewComments from 'features/Preview/PreviewComments/PreviewComments'
import SinglePostPreview from 'features/Preview/SinglePostPreview/SinglePostPreview'
import { useInfiniteFeed } from 'helpers/hooks'
import {
    getLikedHistoryFeed,
    getPlaylistFeed,
    getPurchasesFeed,
    getWatchedHistoryFeed
} from 'services/endpoints/media'
import { type IPost } from 'types/interfaces/ITypes'

export const PreviewMedia = () => {
    const [previewFeed, setPreviewFeed] = useState<IPost[]>([])
    const [showControls, setShowControls] = useState<boolean>(true)

    const swiper = useRef() as any
    const observer = useRef<IntersectionObserver>()

    const {
        userId,
        query,
        clearModal,
        selectedPost,
        selectedSlide,
        minimized,
        handleMinimize,
        updateSelectedPost,
        setCommentsActive,
        commentsActive,
        selectedPostData,
        setSelectedPostData,
        previewLocation
    } = usePreviewContext()

    const handleFetchDataByType = async (type: string, params: any) => {
        if (type === 'purchased') {
            return await getPurchasesFeed(params)
        }
        if (type === 'watched') {
            return await getWatchedHistoryFeed(params)
        }
        if (type === 'playlist') {
            return await getPlaylistFeed(params)
        }

        return await getLikedHistoryFeed(params)
    }

    const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    status
    } = useInfiniteFeed(
    query,
    async (params: any) => {
        return await handleFetchDataByType(previewLocation, params)
    },
    { enabled: false } //  do NOT auto fetch on mount
    )

    useEffect(() => {
        if (data && !isFetching) {
            const tmp = data.pages.map(page => {
                return page.page.data.data
            })

            setPreviewFeed([...tmp.flat()])
        }
    }, [data, isFetching])

    useEffect(() => {
        if (selectedPost !== null) {
            swiper.current?.swiper.slideTo(selectedPost, 0, false)
        }
    }, [selectedPost])

    const lastPostRef = useCallback(
        (node: any) => {
            if (isFetchingNextPage) return
            if (observer.current) observer.current.disconnect()

            observer.current = new IntersectionObserver(
                entries => {
                    if (entries[0].isIntersecting && hasNextPage) {
                        fetchNextPage()
                    }
                },
                { rootMargin: '1500px' }
            )

            if (node) observer.current.observe(node)
        },
        [isFetchingNextPage, hasNextPage]
    )

    if (error) {
        console.log(error)
    }
    status === 'error' && <p>Error: {error?.message}</p>

    return (
        <PreviewWrapper>
            <PreviewClose onClick={clearModal}>
                {minimized ? (
                    <IconStatsClose color='#A3A3A3' />
                ) : (
                    <IconStatsClose />
                )}
            </PreviewClose>
            {!(previewFeed.length === 0) &&
                selectedPostData &&
                selectedPost !== null &&
                selectedSlide !== null && (
                    <Swiper
                        onInit={(core: SwiperCore) => {
                            swiper.current = core.el
                        }}
                        initialSlide={selectedPost}
                        style={{ width: '100%', height: '100%' }}
                        className='mySwiper'
                        direction={'vertical'}
                        speed={700}
                        pagination={false}
                        onSlideChangeTransitionStart={() =>
                            !showControls && setShowControls(true)
                        }
                        onSlideChange={(ctx:any) => {
                            const currentPost = previewFeed[ctx.activeIndex]

                            setSelectedPostData(currentPost)
                        }}
                        observer={true}
                        noSwiping={!showControls || commentsActive || minimized}
                        preventInteractionOnTransition={true}
                    >
                        {previewFeed.map((post, idx) => {
                            if (previewFeed.length === idx + 1) {
                                return (
                                    <SwiperSlide
                                        key={idx}
                                        className={`${
                                            !showControls || minimized
                                                ? 'swiper-no-swiping'
                                                : ''
                                        }`}
                                    >
                                        <PostPreviewWrapper ref={lastPostRef}>
                                            <SinglePostPreview
                                                post={post}
                                                showControls={showControls}
                                                minimizeFn={(
                                                    fileIndex: number,
                                                    type: string
                                                ) => {
                                                    updateSelectedPost(
                                                        idx,
                                                        fileIndex,
                                                        type,
                                                        post
                                                    )
                                                    if (minimized) {
                                                        !showControls &&
                                                            setShowControls(
                                                                true
                                                            )
                                                    }
                                                    handleMinimize(!minimized)
                                                }}
                                                toggleControls={() => {
                                                    setShowControls(
                                                        (
                                                            showControls: boolean
                                                        ) => !showControls
                                                    )
                                                }}
                                                isFirstPost={idx === 0}
                                                hasNextPage={!!hasNextPage}
                                                isInViewport={
                                                    post.id ===
                                                    selectedPostData.id
                                                }
                                                isLastPost={true}
                                            />
                                        </PostPreviewWrapper>
                                    </SwiperSlide>
                                )
                            } else {
                                return (
                                    <SwiperSlide
                                        key={idx}
                                        className={`${
                                            !showControls || minimized
                                                ? 'swiper-no-swiping'
                                                : ''
                                        }`}
                                    >
                                        <SinglePostPreview
                                            post={post}
                                            showControls={showControls}
                                            minimizeFn={(
                                                fileIndex: number,
                                                type: string
                                            ) => {
                                                updateSelectedPost(
                                                    idx,
                                                    fileIndex,
                                                    type,
                                                    post
                                                )
                                                if (minimized) {
                                                    !showControls &&
                                                        setShowControls(true)
                                                }
                                                handleMinimize(!minimized)
                                            }}
                                            toggleControls={() => {
                                                setShowControls(
                                                    (showControls: boolean) =>
                                                        !showControls
                                                )
                                            }}
                                            isFirstPost={idx === 0}
                                            hasNextPage={!!hasNextPage}
                                            isInViewport={
                                                post.id === selectedPostData.id
                                            }
                                            isLastPost={false}
                                        />
                                    </SwiperSlide>
                                )
                            }
                        })}
                    </Swiper>
                )}
            {selectedPostData && (
                <PreviewComments
                    commentsActive={commentsActive}
                    setCommentsActive={setCommentsActive}
                    activeCommentsPostId={selectedPostData?.id}
                    commentCount={selectedPostData?.comment_count}
                    isMyPost={userId === selectedPostData.user?.id}
                />
            )}
        </PreviewWrapper>
    )
}

const PreviewWrapper = styled.div`
    height: 100%;
`

const PreviewClose = styled.div`
    position: fixed;
    top: 2rem;
    right: 1rem;
    z-index: 101;
    padding: 1rem;
`

const PostPreviewWrapper = styled.div`
    height: 100vh;
`
